import React, { useState, useEffect } from 'react';
import { updateRow } from '../lib/db';
import { X, UserPlus, Save, Link as LinkIcon, Unlink } from 'lucide-react';
import { motion } from 'motion/react';
import { useFirebase } from './FirebaseProvider';

interface UserManagementProps {
  onClose: () => void;
}

export const UserManagement: React.FC<UserManagementProps> = ({ onClose }) => {
  const { user, profile } = useFirebase();
  const [uidToLink, setUidToLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLinkAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const targetUserId = uidToLink.trim();

      if (!targetUserId) {
        setError('Por favor, ingresa un ID de usuario válido.');
        setLoading(false);
        return;
      }

      if (targetUserId === user.uid) {
        setError('No puedes vincular tu propia cuenta.');
        setLoading(false);
        return;
      }

      // Update the current user's profile to link to the target user
      await updateRow('profiles', user.uid, { linkedTo: targetUserId });

      setSuccess(`Cuenta vinculada exitosamente. Ahora verás y gestionarás sus datos.`);
      setUidToLink('');
    } catch (err: any) {
      console.error("Error linking account:", err);
      setError('Error al vincular la cuenta. Verifica que el ID sea correcto.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkAccount = async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateRow('profiles', user.uid, { linkedTo: null });
      setSuccess('Cuenta desvinculada exitosamente. Ahora verás tus propios datos.');
    } catch (err: any) {
      console.error("Error unlinking account:", err);
      setError('Error al desvincular la cuenta.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-zinc-900 border border-zinc-800 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <LinkIcon size={20} className="text-emerald-500" />
            Cuentas Vinculadas
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-all text-zinc-500 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-sm text-zinc-400">
            Puedes vincular tu cuenta a la de otro usuario (por ejemplo, tu cónyuge) para ver y gestionar los mismos datos financieros.
          </p>

          <div className="bg-zinc-950 border border-zinc-800 p-4 rounded-xl">
            <p className="text-xs text-zinc-500 uppercase font-bold tracking-wider mb-1">Tu ID de Usuario</p>
            <div className="flex items-center justify-between gap-2">
              <code className="text-sm text-emerald-500 font-mono bg-emerald-500/10 px-2 py-1 rounded">{user?.uid}</code>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(user?.uid || '');
                  alert('ID copiado al portapapeles');
                }}
                className="text-xs bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-1.5 rounded-lg transition-all"
              >
                Copiar
              </button>
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              Comparte este ID con la persona que deseas que se vincule a tu cuenta.
            </p>
          </div>

          {profile?.linkedTo ? (
            <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-xl space-y-4">
              <div className="flex items-center gap-3 text-emerald-500">
                <LinkIcon size={24} />
                <div>
                  <p className="font-bold">Cuenta Vinculada</p>
                  <p className="text-xs text-emerald-500/80">Estás viendo los datos de otro usuario.</p>
                </div>
              </div>
              <button
                onClick={handleUnlinkAccount}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 font-bold py-2.5 rounded-xl transition-all"
              >
                <Unlink size={18} />
                Desvincular Cuenta
              </button>
            </div>
          ) : (
            <form onSubmit={handleLinkAccount} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">
                  ID del usuario a vincular
                </label>
                <input
                  type="text"
                  value={uidToLink}
                  onChange={(e) => setUidToLink(e.target.value)}
                  placeholder="Pega el ID del otro usuario aquí"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none font-mono"
                  required
                />
                <p className="text-xs text-zinc-500 mt-2">
                  Pídele al otro usuario que te comparta su ID desde esta misma pantalla.
                </p>
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}
              {success && <p className="text-emerald-500 text-sm">{success}</p>}

              <button
                type="submit"
                disabled={loading || !uidToLink.trim()}
                className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-black font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    <LinkIcon size={18} />
                    Vincular Cuenta
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};
